import type { QuantChartModel as DrawModel } from '@/ui/lib/quantChart/types'

import type { CandlePoint, PreviewOverlay } from './types'

/**
 * The Config preview's chart model builder (real preview answers, plus the
 * parity fixtures). Folds the streamed candles plus the latest overlay frame into the chart
 * renderer's model. Overlay lines are time-keyed on the wire; the renderer
 * wants one value per candle, so each line is laid onto the candle times
 * (bars with no point, such as a new tick bar, stay `null`).
 */
export function buildPreviewModel(
  candles: CandlePoint[],
  overlay: PreviewOverlay | null,
  digits: number,
  options?: { legend?: 'overlay' | 'none' },
): DrawModel {
  const lines = (overlay?.lines ?? []).map((line) => {
    const byTime = new Map<number, number>()
    for (const p of line.points) byTime.set(p.time, p.value)
    return { id: line.id, color: line.color, values: candles.map((c) => byTime.get(c.time) ?? null) }
  })
  return {
    candles,
    levels: (overlay?.levels ?? []).map((l) => ({
      price: l.price,
      timeStart: l.time_start,
      timeEnd: l.time_end,
      color: l.color,
      dashed: l.dashed,
      label: l.label,
    })),
    zones: (overlay?.zones ?? []).map((z) => ({
      timeStart: z.time_start,
      timeEnd: z.time_end,
      low: z.lo,
      high: z.hi,
      color: z.color,
      ...(z.state !== undefined ? { state: z.state } : {}),
      ...(z.label !== undefined ? { label: z.label } : {}),
      // An explicit server alpha wins; the state table is the fallback when it is absent.
      ...(typeof z.alpha === 'number' ? { alpha: z.alpha } : {}),
    })),
    markers: (overlay?.markers ?? []).map((m) => ({ time: m.time, above: m.above, kind: m.kind, text: m.text })),
    lines,
    bands: [],
    circles: [],
    sessions: (overlay?.sessions ?? []).map((s) => ({ timeStart: s.time_start, timeEnd: s.time_end })),
    legend: options?.legend === 'none' ? [] : (overlay?.legend ?? []),
    equity: null,
    digits,
  }
}
